import { useTranslation } from '@thoughtspot/i18n';
import { Button } from '@thoughtspot/radiant-react/widgets/button/button';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { Input } from '@thoughtspot/radiant-react/widgets/input/input';
import { Horizontal } from '@thoughtspot/radiant-react/widgets/layout';
import { TextArea } from '@thoughtspot/radiant-react/widgets/text-area';
import {
    Colors,
    Typography,
} from '@thoughtspot/radiant-react/widgets/typography';
import _ from 'lodash';
import * as React from 'react';
import { useState } from 'react';
import { EditableList, Item } from '../../editable-list/editable-list';
import { isEmail } from '../../email/email.util';
import {
    InvitedUser,
    InviteUsersContentProps,
} from '../invite-users.interface';
import styles from './invite-users-content.module.scss';

export const InviteUsersContent = ({
    invitedUsers,
    onEmailAdd,
    onEmailDelete,
    onDescriptionAdd,
    inputEmail,
    setInputEmail,
    inputEmailHasError,
    setInputEmailHasError,
    isCopilotEnabled = false,
}: InviteUsersContentProps) => {
    const { t } = useTranslation();
    const [textAreaDescription, setTextAreaDescription] = useState('');
    const [emailList, setEmailList] = useState([]);

    React.useEffect(() => {
        const mappedInvites: Item[] = invitedUsers.map(
            (invite: InvitedUser) => {
                return {
                    avatar: {
                        name: invite.address,
                        showName: false,
                        src: '',
                    },
                    label: invite.address,
                    errorMessage: invite.errorMessage,
                    isDisabled: !!invite.errorMessage,
                };
            },
        );
        setEmailList(mappedInvites);
    }, [invitedUsers]);

    const handleInputEmailError = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        if (_.isEmpty(event.target.value) || isEmail(event.target.value)) {
            setInputEmailHasError(false);
        } else {
            setInputEmailHasError(true);
        }
    };

    const handleTextAreaDescriptionChange = (
        event: React.ChangeEvent<HTMLTextAreaElement>,
    ) => {
        setTextAreaDescription(event.target.value);
        onDescriptionAdd(event.target.value);
    };

    const handleAddClick = () => {
        if (isEmail(inputEmail)) {
            onEmailAdd(inputEmail);
            setInputEmail('');
        }
    };

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputEmail(event.target.value);
        setInputEmailHasError(false);
    };

    return (
        <div className={styles.inviteUsers} data-testid="invite-users-content">
            <div className={styles.addUser}>
                <Typography variant="p" className={styles.title}>
                    {!isCopilotEnabled
                        ? t('homehub.invite_others.modal.title')
                        : t('homehub.invite_others_for_edge.modal.title')}
                </Typography>
                <Typography variant="p" className={styles.fieldTitle}>
                    {t('homehub.invite_others.add_emails_header')}
                </Typography>
                <Horizontal
                    vAlignContent="stretch"
                    spacing="d"
                    className={styles.addUserInputWrapper}
                >
                    <Input.Text
                        className={styles.addUserInput}
                        ariaLabel={inputEmail}
                        value={inputEmail}
                        data-testid="title-input-box"
                        onChange={handleOnChange}
                        onEnter={handleAddClick}
                        onBlur={handleInputEmailError}
                    />
                    <Button
                        icon={IconID.PLUS}
                        type="secondary"
                        size="s"
                        isDisabled={!isEmail(inputEmail)}
                        onClick={handleAddClick}
                        data-testid="invite-add-button"
                    />
                </Horizontal>
                {inputEmailHasError && (
                    <Typography
                        variant="p-small"
                        color={Colors.failure}
                        className={styles.addUserError}
                    >
                        {t('homehub.invite_others.invalid_email')}
                    </Typography>
                )}
                <Typography variant="p">
                    {t('homehub.invite_others.add_message')}
                </Typography>
                <TextArea
                    ariaLabel={textAreaDescription}
                    value={textAreaDescription}
                    name="textarea"
                    onChange={handleTextAreaDescriptionChange}
                />
            </div>
            {emailList && (
                <EditableList items={emailList} onRemove={onEmailDelete} />
            )}
        </div>
    );
};
